import React from 'react';
import { computed } from 'mobx';
import flatMap from 'lodash/flatMap';
import get from 'lodash/get';
import minBy from 'lodash/minBy';
import startCase from 'lodash/startCase';
import uniq from 'lodash/uniq';
import moment from 'moment';
import Promise from 'bluebird';
import $auth from 'app/stores/$auth';

import { deserializeCidrDimension } from '@kentik/ui-shared/alertingManager/utils';
import api from 'core/util/api';
import { showSuccessToast } from 'core/components';
import { buildFilterGroup, mergeFilterGroups } from 'core/util/filters';
import { DEFAULT_DATETIME_FORMAT } from 'core/util/dateUtils';
import { Collection } from 'core/model';
import { getTabPref } from 'core/util/table';
import formatMetricValue from 'app/util/formatMetricValue';
import { getMetricOption } from 'app/util/policies';
import transformPolicyFilters from 'app/util/alerting/transformPolicyFilters';
import { getQueryFilter } from 'app/stores/insight/insightUtils';
import { addFilters } from 'app/stores/query/FilterUtils';

import {
  ALERT_BASELINE_REASONS,
  ALERTING_EXPORT_PER_PAGE_COUNT_DEFAULT,
  ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY,
  ALERT_SEVERITY_COLORS,
  ALERT_SEVERITY_LABELS,
  ALERT_STATE_COLORS,
  ALERT_STATE_ICONS,
  ALERT_STATE_LABELS,
  ALERT_SEVERITY_RANKS,
  DIMENSION_LOOKUP_PATHS,
  DIMENSION_TO_LABEL,
  POLICY_APPLICATION_LABELS,
  POLICY_APPLICATIONS,
  KEVENT_INTERNAL_POLICY_APPLICATION_LABEL,
  EVENT_POLICY_METRICS,
  EVENT_POLICY_TYPES,
  COMPARISON_DIRECTIONS,
  ALERT_API_STATES
} from '@kentik/ui-shared/alerting/constants';
import { ALERT_DIMENSIONS_WHITELIST, ALERT_METRIC_OPTIONS } from 'app/util/constants';

import SudoTaggedText from 'app/components/SudoTaggedText';
import { convertThresholdFiltersToQueryFilters } from './policyUtils';
import { getThresholdValueForConditionType, getRawCapacity } from './alertUtils';

import FlowPolicyCollection from './FlowPolicyCollection';
import AllPolicyCollection from './AllPolicyCollection';
import AlertCollection from './AlertCollection';
import AlertModel from './AlertModel';
import AlertStatsCollection from './AlertStatsCollection';
import ddosFactorQueries from './DdosFactorQueries';
import AutoAckCollection from './AutoAckCollection';
import SilenceCollection from './SilenceCollection';
import NmsPolicyCollection from './nms/NmsPolicyCollection';
import EventPolicyCollection from './kevent/EventPolicyCollection';

// Templates
import FlowPolicyTemplateCollection from './FlowPolicyTemplateCollection';
import NmsPolicyTemplateCollection from './nms/NmsPolicyTemplateCollection';
import EventPolicyTemplateCollection from './kevent/EventPolicyTemplateCollection';

const getPercentage = (base, compare) => {
  const pct = (compare >= base ? (compare - base) / base : (base - compare) / base) * 100;
  return Math.round(pct * 100) / 100; // 2 decimals of precision
};
export class AlertingStore {
  collection = new AlertCollection();

  stats = new AlertStatsCollection();

  // This collection contains all alert policies excluding NMS policies
  _flowPolicyCollection = new FlowPolicyCollection();

  // This collection contains all NMS alert policies (excluding legacy threshold and up/down policies)
  _nmsPolicyCollection = new NmsPolicyCollection();

  _eventPolicyCollection = new EventPolicyCollection();

  // This collection contains all policy types, both standard and nms together in one collection
  _allPolicyCollection = new AllPolicyCollection([], {
    wrappedPolicyCollections: [
      // if we ever get more types, they'll go in here
      { collection: this._flowPolicyCollection },
      { collection: this._nmsPolicyCollection, permission: 'alerts.nmsNative.enabled' },
      { collection: this._eventPolicyCollection, permission: 'alerts.eventAlerting.enabled' }
    ]
  });

  _flowPolicyTemplateCollection = new FlowPolicyTemplateCollection();

  _nmsPolicyTemplateCollection = new NmsPolicyTemplateCollection();

  _eventPolicyTemplateCollection = new EventPolicyTemplateCollection();

  policyTemplateCollection = new AllPolicyCollection([], {
    wrappedPolicyCollections: [
      // if we ever get more types, they'll go in here
      { collection: this._flowPolicyTemplateCollection },
      { collection: this._nmsPolicyTemplateCollection, permission: 'alerts.nmsNative.enabled' },
      { collection: this._eventPolicyTemplateCollection, permission: 'alerts.eventAlerting.enabled' }
    ]
  });

  autoAckCollection = new AutoAckCollection();

  silenceCollection = new SilenceCollection();

  constructor(options = {}) {
    Object.assign(this, options);
  }

  get policyCollection() {
    // Until the NMS alerting feature is ready, we will continue to use the _flowPolicyCollection
    const nmsAlertingEnabled = $auth.hasPermission('alerts.nmsNative.enabled', { overrideForSudo: false });
    // While there is a separate `alerts.eventAlerting.enabled` permission for Event policies, they are a subset of NMS policies.

    // At this time, MKP does *not* support any NMS or Event policies.
    // This condition ensures MKP tenants' alerts will load proper
    if ($auth.isSubtenantUser) {
      return this._flowPolicyCollection;
    }

    return nmsAlertingEnabled ? this._allPolicyCollection : this._flowPolicyCollection;
  }

  @computed
  get ddosTemplates() {
    return new FlowPolicyTemplateCollection(
      this._flowPolicyTemplateCollection.get().filter((template) => template.isDdosPolicy)
    );
  }

  @computed
  get policyRuleIdOptions() {
    const options = this.policyCollection.generateSelectOptions({
      labelKey: 'name',
      valueKey: 'ruleId',
      sortBy: 'name',
      unfiltered: true
    });
    return options.filter((o) => !!o.value);
  }

  @computed
  get flowPolicyIdOptions() {
    return this._flowPolicyCollection.policyOptions;
  }

  // Check if pattern dimension key-value pairs match candidate key-value pairs
  matchesKeyValuePattern({
    target = {},
    pattern = {},
    strict = true,
    omitFalsey = true,
    falseyValues = ['', undefined, null, '---']
  }) {
    const patternKeys = Object.keys(pattern);
    const targetKeys = Object.keys(target).filter((key) => !omitFalsey || !falseyValues.includes(target[key]));

    // Omitting falsey entries is useful, since we do this when creating silences or suppressions
    // Prevent false positives by checking if the key is actually one of the target keys, not just missing
    const matchesPattern = patternKeys.every((key) => pattern[key] === target[key] && targetKeys.includes(key));
    const hasEqualKeys = patternKeys.length === targetKeys.length;

    // If strict, the key counts should match too (no excess target keys)
    return strict ? hasEqualKeys && matchesPattern : matchesPattern;
  }

  findActiveSilenceForAlert(alertModel) {
    return this.silenceCollection.unfiltered.find((silenceModel) => {
      const reqRuleMatch = silenceModel.ruleId && silenceModel.ruleId !== '';
      const reqKeyValueMatch =
        silenceModel.dimensionToKeyPart && Object.keys(silenceModel.dimensionToKeyPart).length > 0;

      // Malformed, can't be a match
      if (!reqRuleMatch && !reqKeyValueMatch) {
        return false;
      }

      // expired, can't be a match
      if (silenceModel.expired) {
        return false;
      }

      // From here on, we can assume at least one match condition is required.
      const matchesRule = silenceModel.ruleId === alertModel.ruleId;
      const matchesKeyValue = this.matchesKeyValuePattern({
        pattern: silenceModel.dimensionToKeyPart,
        target: alertModel.pattern.dimensionToKeyPart
      });

      // If one condition is not required, we know the other is. Treat non-required conditions as true.
      return (reqRuleMatch ? matchesRule : true) && (reqKeyValueMatch ? matchesKeyValue : true);
    });
  }

  fetchActiveAlerts({ application, ruleId, options = {} }) {
    return api.post('/api/ui/alertingManager/alarms', {
      data: {
        filter: {
          applications: [application],
          ruleIds: ruleId ? [ruleId] : undefined,
          ackStates: options.ackStates || [],
          states: [ALERT_API_STATES.ACTIVE],
          severities: [],
          active: {
            start: null,
            end: null
          },
          includeArchived: false
        },
        pagination: {
          includeTotalCount: true,
          offset: 0,
          limit: options?.pagination?.limit || 1000
        }
      }
    });
  }

  getActiveAlertCount({ application, ruleId, options }) {
    return this.fetchActiveAlerts({ application, ruleId, options: { pagination: { limit: 1 }, ...options } }).then(
      (data) => parseInt(data?.pagination?.total_count) || 0
    );
  }

  fetchDebugPolicyData(options) {
    const { policyId } = options;
    const url = `/api/ui/alerting/policies/debug/${policyId}`;
    return api.get(url);
  }

  fetchDebugAlertData(alertModel, { sentBetween }) {
    const body = { ruleId: alertModel.ruleId, key: { filter: {} }, strict: false, withContext: true };
    const { dimensionToKeyPart = {} } = alertModel.pattern || {};

    Object.entries(dimensionToKeyPart).forEach(([key, value]) => {
      body.key.filter[key] = { equals: value };
    });

    if (sentBetween) {
      body.sentBetween = sentBetween;
    }

    return api
      .post('/api/ui/alertingManager/alarms/debug', { body })
      .then(({ triggers }) => (Array.isArray(triggers) ? triggers : []));
  }

  convertExplorerQueryToPolicyAttrs(query = {}) {
    const { metrics = [] } = query;
    const isEventAlertingEnabled = $auth.hasPermission('alerts.eventAlerting.enabled', { overrideForSudo: false });

    if (isEventAlertingEnabled && metrics[0] === EVENT_POLICY_METRICS.SYSLOG) {
      return this.buildEventPolicyFromExplorerQuery({
        ...query,
        eventType: EVENT_POLICY_TYPES.SYSLOG
      });
    }

    if (isEventAlertingEnabled && metrics[0] === EVENT_POLICY_METRICS.SNMP_TRAP) {
      return this.buildEventPolicyFromExplorerQuery({
        ...query,
        eventType: EVENT_POLICY_TYPES.SNMP_TRAP
      });
    }

    return this.buildTrafficPolicyFromExplorerQuery(query);
  }

  buildTrafficPolicyFromExplorerQuery({ selected_devices, filters, dimensions = [], metrics = [] }) {
    const supportedMetrics = [];
    const unsupportedMetrics = [];

    metrics.forEach((metric) => {
      const match = ALERT_METRIC_OPTIONS.find((option) => option.unit === metric);

      if (match) {
        supportedMetrics.push(match.value);
      } else {
        unsupportedMetrics.push({ value: metric, label: this.store.$dictionary.get(`units.${metric}`) || metric });
      }
    });

    const supportedDimensions = [];
    const unsupportedDimensions = [];

    dimensions.forEach((dimension) => {
      if (ALERT_DIMENSIONS_WHITELIST.has(dimension)) {
        supportedDimensions.push(dimension);
      } else {
        unsupportedDimensions.push({ label: this.getDimensionLabel(dimension), value: dimension });
      }
    });

    const policyAttrs = {
      application: POLICY_APPLICATIONS.CORE,
      selected_devices,
      silenced: false,
      metrics: supportedMetrics.slice(0, 3),
      daysToExpire: 90,
      evaluationPeriod: 120,
      filters,
      dimensions: uniq(supportedDimensions)
    };

    return {
      policyAttrs,
      unsupportedMetrics,
      unsupportedDimensions
    };
  }

  getDefaultEventTypeDimensions(eventType) {
    return this.store.$dictionary.get('appProtocolQuery.requiredCols')[`event_${eventType}`] || [];
  }

  buildEventPolicyFromExplorerQuery({ dimensions = [], eventType, ...attributes }) {
    const defaultDimensions = this.getDefaultEventTypeDimensions(eventType);

    const policyAttrs = {
      ...attributes,
      dimensions: uniq([...defaultDimensions, ...dimensions]),
      metrics: ['fps'],
      application: POLICY_APPLICATIONS.KEVENT,
      eventType
    };

    return { policyAttrs };
  }

  // dictionary dependency
  getRawDataKey(query, outsortOverride) {
    const aggregates = query.get('aggregates');
    const outsort = outsortOverride || query.get('outsort');
    const rawAgg = aggregates.find(
      (agg) =>
        agg.raw &&
        agg.column &&
        (agg.name === outsort || (outsort.startsWith('sum_logsum') && agg.unit === outsort.replace('sum_logsum_', '')))
    );

    let newColName = '';
    if (rawAgg && rawAgg.column) {
      newColName = rawAgg.column.replace('f_sum_', '').replace('bytes', 'bits').replace('trautocount', 'flows');
      if (
        !this.store.$dictionary.get('countColumns').includes(rawAgg.column) &&
        !this.store.$dictionary.get('countRegex').some((regex) => new RegExp(regex).test(rawAgg.column)) &&
        !rawAgg.is_count
      ) {
        newColName += '_per_sec';
      }
    }

    return newColName;
  }

  // dictionary dependency
  getRawDataKeyByUnit(unit = 'bytes', rawData) {
    const keyMap = {
      bytes: 'both_bits_per_sec',
      packets: 'both_pkts_per_sec'
    };

    const rawDataKeys = rawData ? Object.keys(rawData) : [];

    if (rawDataKeys.length === 1) {
      return rawDataKeys[0];
    }

    return keyMap[unit] || this.store.$dictionary.unitToKdeColumn[unit] || unit;
  }

  @computed
  get isDdosConfigured() {
    const policies = this.policyCollection.unfiltered;
    return policies.length > 0 && policies.find((policy) => policy.isDdosPolicy);
  }

  getChildrenRuleIds({ ruleIds = [], tenants = [] }) {
    const parentIds = this.policyCollection.unfiltered
      .filter((p) => ruleIds.includes(p.ruleId))
      .map(({ id }) => `${id}`);

    const tenantPolicies = this.store.$mkp.tenants.getSubpolicies(tenants) || [];
    const childPolicies = tenantPolicies.filter(({ parentId }) => parentIds.includes(`${parentId}`));

    return childPolicies.map((p) => p.activationSettings?.ruleId).filter(Boolean);
  }

  getSortedRuleIds({ field, direction = 'asc' }) {
    let policiesToSort = this.policyCollection.unfiltered;

    if (field) {
      policiesToSort = policiesToSort
        .map((policy) => {
          const sortByValue = policy[field] || policy.get(field);
          return {
            ruleId: policy.ruleId || policy.activationSettings?.ruleId,
            [field]: sortByValue ?? ''
          };
        })
        .sort((policyA, policyB) => {
          const valueA = `${policyA[field]}`;
          const valueB = `${policyB[field]}`;
          return direction === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
        });
    }

    // Return array of rule IDs
    return policiesToSort.map((p) => p.ruleId).filter(Boolean);
  }

  get isNmsEnabled() {
    return this.store.$metrics.availableMetricsCollection.size > 0 && !this.store.$app.isSubtenant;
  }

  get policyTypeOptions() {
    const options = [
      { value: POLICY_APPLICATIONS.CORE, label: POLICY_APPLICATION_LABELS.core },
      { value: POLICY_APPLICATIONS.CLOUD, label: POLICY_APPLICATION_LABELS.cloud },
      { value: POLICY_APPLICATIONS.DDOS, label: POLICY_APPLICATION_LABELS.ddos }
    ];

    // Add NMS if allowed
    if (this.isNmsEnabled) {
      options.unshift({ value: POLICY_APPLICATIONS.METRIC, label: POLICY_APPLICATION_LABELS.kmetrics });

      if ($auth.isSudoer) {
        // Sudo-only filter for KEvent policies specifically
        options.unshift({
          value: POLICY_APPLICATIONS.KEVENT,
          label: (
            <SudoTaggedText tagProps={{ small: true }} display="inline-flex">
              {KEVENT_INTERNAL_POLICY_APPLICATION_LABEL}
            </SudoTaggedText>
          )
        });
      }
    }

    return options;
  }

  @computed
  get policyTemplateOptions() {
    const templates = this.policyTemplateCollection.get();

    return templates
      .map((template) => ({
        label: template.get('name'),
        value: template.get('id')
      }))
      .sort((a, b) => {
        const aLabel = a.label.toLowerCase ? a.label.toLowerCase() : a.label;
        const bLabel = b.label.toLowerCase ? b.label.toLowerCase() : b.label;
        return aLabel > bLabel ? 1 : -1;
      });
  }

  /**
   * Clear one or more alertmanager alerts
   * @param {Model or [Model, Model, ...]} alertModels
   * @param {Boolean} toast
   * @returns Promise
   */
  clearAlarms(alertModels, { toast = true, comment = '' }) {
    const models = Array.isArray(alertModels) ? alertModels : [alertModels];
    const activeModels = models.filter((model) => model.isActive);
    const activeIds = activeModels.map((alertModel) => alertModel.id);

    if (activeIds.length === 0) {
      return Promise.resolve([]);
    }

    return api.post('/api/ui/alertingManager/alarms/clear', { data: { alertIds: activeIds, comment } }).then(() => {
      activeModels.forEach((model) => model.setCleared());
      if (toast && models.length > 0) {
        const toastMessage =
          models.length === 1 ? 'Alert successfully cleared.' : `${activeIds.length} alerts succssfully cleared.`;
        showSuccessToast(toastMessage);
      }
      return alertModels;
    });
  }

  // Auto acknowledgements, suppressions, and silences all use the same basic data shape for a model
  buildActionModelFromAlert = (models, options = {}) => {
    const { collection, userId, startTime, endTime, comment } = options;

    // Collection is required
    if (!collection) {
      return false;
    }

    const alertModels = Array.isArray(models) ? models : [models];
    const baseSettings = {
      userId: userId || this.store.$auth.activeUser.id,
      startTime: startTime || null, // Let API set start time to avoid request getting rejected
      endTime: endTime || moment.utc().add(3, 'hours').toISOString()
    };

    if (comment) {
      baseSettings.comment = comment;
    }

    return alertModels.map((alertModel) => {
      const { pattern } = alertModel;
      const { ruleId, dimensionToKeyPart } = pattern || {};
      const data = { ...baseSettings, ruleId, key: { value: dimensionToKeyPart } };
      return collection.build(data, { deserialize: false });
    });
  };

  buildAutoAcksFromAlerts = (models, options = {}) =>
    this.buildActionModelFromAlert(models, { collection: this.autoAckCollection, ...options });

  buildSilencesFromAlerts = (models, options = {}) =>
    this.buildActionModelFromAlert(models, { collection: this.silenceCollection, ...options });

  acknowledgeAlarm(models, options = {}) {
    const { comment, autoAck, silence, toast = true } = options;
    const ACK_PATH = '/api/ui/alertingManager/alarms/ack';
    const alertModels = Array.isArray(models) ? models : [models];

    if (alertModels.length === 0) {
      return Promise.resolve();
    }

    const data = { alertIds: alertModels.map((alert) => alert.id) };

    if (typeof comment === 'string' && comment.trim() !== '') {
      data.comment = comment;
    }

    // Process the manual acks
    return api
      .post(ACK_PATH, { data })
      .then(() => {
        alertModels.forEach((alertModel) => alertModel.setAcknowledged());
        if (toast) {
          const toastMessage =
            alertModels.length === 1
              ? 'Alert acknowledged successfully'
              : `${alertModels.length} Alerts acknowledged successfully`;
          showSuccessToast(toastMessage);
        }
      })
      .then(() => {
        // Process auto acks, if any
        if (!autoAck) {
          return Promise.resolve();
        }
        const autoAckModels = this.buildAutoAcksFromAlerts(alertModels, {
          endTime: autoAck.endTime,
          comment: data.comment
        });
        return Promise.all(autoAckModels.map((autoAckModel) => autoAckModel.save({}, { toast: false })));
      })
      .then(() => {
        // Process silences, if any
        if (!silence) {
          return Promise.resolve();
        }
        const silenceModels = this.buildSilencesFromAlerts(alertModels, { endTime: silence.endTime });
        return Promise.all(silenceModels.map((silenceModel) => silenceModel.save({}, { toast: false }))).then(() => {
          const toastMessage =
            alertModels.length === 1
              ? 'Alert silence successfully created. It will take effect in a few minutes.'
              : `${alertModels.length} alert silences successfully created. It will take effect in a few minutes.`;
          showSuccessToast(toastMessage);
        });
      });
  }

  unacknowledgeAlarm(alertModels = [], toast = true) {
    const alerts = Array.isArray(alertModels) ? alertModels : [alertModels];
    const alertIds = alerts.map((alert) => alert.id);
    return api.post('/api/ui/alertingManager/alarms/unack', { data: { alertIds } }).then(() => {
      alerts.forEach((alertModel) => {
        alertModel.setUnacknowledged();
      });

      if (toast && alerts.length > 0) {
        const toastMessage =
          alerts.length === 1 ? 'Alert Acknowledgement Removed.' : `${alerts.length} Alert Acknowledgements Removed.`;
        showSuccessToast(toastMessage);
      }

      return alerts;
    });
  }

  attachNotificationsToPolicies(policies) {
    const data = { policies: policies.map((policy) => policy.serialize()) };

    return api.post('/api/ui/notifications/policyMappings', { data });
  }

  // valid filters are: startDate, endDate, lookback, includeSubpolicies, applications
  getSummaryTotals(filters) {
    return api.post('/api/ui/alerting/summary-totals', { data: filters });
  }

  // valid filters are: startDate, endDate, lookback, applications
  loadOverviewData(filters) {
    return api.post('/api/ui/alerting/overview', { data: { ...filters, ...filters.selectedTime } }).then((data) => {
      const policyStatsCollection = new Collection(data.policies);
      policyStatsCollection.sort('triggerCount', 'desc');

      return {
        ...data,
        policies: policyStatsCollection
      };
    });
  }

  getAlertById(id, options = {}) {
    return api.get(`/api/ui/alertingManager/alarms/${id}`, options);
  }

  calculateEventEndTime({ endTime, threshold }) {
    const inactivityTimeUntilClear = parseInt(get(threshold, 'activationSettings.inactivityTimeUntilClear', 0)) || 0;

    if (!endTime) {
      return null;
    }

    return moment(endTime).subtract(inactivityTimeUntilClear, 'seconds').valueOf();
  }

  getSeverityChange(fromSeverity, toSeverity, options) {
    const { upLabel = '↑', downLabel = '↓', sameLabel = '' } = options || {};
    const fromRank = ALERT_SEVERITY_RANKS[this.getSeverityLabel(fromSeverity)];
    const toRank = ALERT_SEVERITY_RANKS[this.getSeverityLabel(toSeverity)];

    if (fromRank < toRank) {
      return upLabel;
    }

    if (fromRank > toRank) {
      return downLabel;
    }

    return sameLabel;
  }

  getSeverityLabel(severity) {
    const key = ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY[severity] || severity;
    return ALERT_SEVERITY_LABELS[key] || 'Unknown';
  }

  getSeverityColor(severity) {
    const key = ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY[severity] || severity;
    return ALERT_SEVERITY_COLORS[key];
  }

  getStateDisplay(state) {
    return ALERT_STATE_LABELS[state] || null;
  }

  getStateColor(state) {
    return ALERT_STATE_COLORS[state];
  }

  getStateIcon(state) {
    return ALERT_STATE_ICONS[state] || null;
  }

  getBaselineDescription(baselineTrigger) {
    return ALERT_BASELINE_REASONS[baselineTrigger]?.message || null;
  }

  // note - we may come here from $insights and be passed an alarm object that is flat (ie not a model)
  lookupValue(alarm, dimension, value, model) {
    const dimensionToKeyDetail = alarm.dimensionToKeyDetail || model?.dimensionToKeyDetail || {};

    if (DIMENSION_LOOKUP_PATHS[dimension]) {
      const path = DIMENSION_LOOKUP_PATHS[dimension];
      const lookupValue = get(dimensionToKeyDetail, `${dimension}.${path}`);

      if (lookupValue) {
        switch (path) {
          case 'asn.description':
            return `AS ${value} (${lookupValue})`;

          case 'interface.snmpDescription':
            return `${lookupValue} (${value})`;

          default:
            return lookupValue;
        }
      }
    }

    return this.store.$insights.lookupDisplayName(dimension, value);
  }

  getMetrics(alert) {
    let policyMetrics = [];

    if (alert.policyObject) {
      policyMetrics = get(alert.policyObject, 'metrics', []);
    } else if (alert.get) {
      policyMetrics = alert.get('metrics');
    }

    const metricToValue = alert.metricToValue || alert.get('metricToValue') || {};

    let metrics = policyMetrics.filter((m) => metricToValue[m] !== undefined);
    if (metrics.length === 0) {
      metrics = Object.keys(metricToValue);
    }
    return metrics;
  }

  hasMissingValue(alertModel, onMetric) {
    if (!alertModel.threshold) {
      return false;
    }

    const { conditions = [] } = alertModel.threshold;

    return conditions.some((condition) => {
      const { type, direction, metric = alertModel.primaryMetric } = condition;

      return type === 'keyNotInTop' && direction === COMPARISON_DIRECTIONS.TO_CURRENT && metric === onMetric;
    });
  }

  // note - we may come here from $insights and be passed an alarm object that is flat (ie not a model)
  getDimension(alarm, dimension, value = this.getDimensionValue(alarm, dimension)) {
    if (value === undefined) {
      return null;
    }

    const lookup = this.lookupValue(alarm, dimension, value);
    let label = this.getDimensionLabel(dimension);

    if (dimension === 'Traffic') {
      label = 'Traffic';
    }

    if (alarm.isMetricPolicy) {
      label = alarm.policyMeasurement?.storage?.Dimensions?.[dimension]?.Label || dimension;
    }

    return {
      key: dimension,
      label,
      value,
      lookup,
      formatted: `${label}: ${lookup}`,
      toString() {
        return this.formatted;
      }
    };
  }

  // note - we may come here from $insights and be passed an alarm object that is flat (ie not a model)
  getDimensionValue(alarm, dimension) {
    const dimensionToKeyPart = (alarm.get ? alarm.get('dimensionToKeyPart') : alarm.dimensionToKeyPart) || {};

    if (alarm.isMetricPolicy) {
      return get(alarm, `reconDimensions.${dimension}`);
    }

    if (this.store.$dictionary.get('cidrMetrics').includes(dimension)) {
      const keyPart = Object.keys(dimensionToKeyPart).find((key) => key.startsWith(dimension));
      return dimensionToKeyPart[keyPart];
    }

    return dimensionToKeyPart[dimension];
  }

  getBaseline(alertModel) {
    const { baselineValue, baselineSource, metricToValue } = alertModel;

    if (!baselineValue && !baselineSource) {
      return null;
    }

    const value = !this.hasMissingValue(alertModel, alertModel.primaryMetric)
      ? metricToValue[alertModel.primaryMetric]
      : null;
    const { value: formattedValue, metric: formattedMetric } = formatMetricValue(
      baselineValue,
      alertModel.primaryMetric
    );

    return {
      metric: alertModel.primaryMetric,
      value: baselineValue,
      over: Number.isFinite(value) ? value >= baselineValue : null,
      percentage: Number.isFinite(value) ? getPercentage(baselineValue, value) : null,
      formatted: `${formattedValue} ${formattedMetric}`,
      formattedValue,
      formattedMetric,
      toString() {
        return this.formatted;
      }
    };
  }

  // note - we may come here from $insights and be passed an alarm object that is flat (ie not a model)
  getCapacity(alarm) {
    const capacity = getRawCapacity(alarm);
    if (capacity === 0) {
      return undefined;
    }

    const metric = 'bits';
    const { value: formattedValue, metric: formattedMetric, formatted } = formatMetricValue(capacity, metric);

    const value = get(alarm, `metricToValue.${metric}`);

    return {
      metric,
      value: capacity,
      percentage: Math.round((value / capacity) * 100),
      formatted,
      formattedValue,
      formattedMetric,
      toString() {
        return this.formatted;
      }
    };
  }

  getAlertThresholdValues(alert, thresholdOverride) {
    const alertModel = alert?.get ? alert : new AlertModel(alert);
    const { triggeredThreshold, primaryMetric } = alertModel;
    const policyThreshold = thresholdOverride || triggeredThreshold;
    const { severity, conditions = [], direction } = policyThreshold || {};

    return conditions
      .map((condition) => {
        const thresholdConditionValues = getThresholdValueForConditionType(condition, policyThreshold, alertModel);

        if (!thresholdConditionValues) {
          return null;
        }

        const { thresholdValue, metricValue, ratio = {} } = thresholdConditionValues;
        const { metric = primaryMetric, operator, comparisonValue, type } = condition;
        const { value: formattedValue, metric: formattedMetric } = formatMetricValue(thresholdValue, metric);
        const { value: formattedOtherValue, metric: formattedOtherMetric } = formatMetricValue(metricValue, metric);

        let over = metricValue >= thresholdValue;
        over = direction === COMPARISON_DIRECTIONS.TO_HISTORY ? over : !over;

        const percentage = getPercentage(thresholdValue, metricValue) || 0;
        const diff = Math.abs(metricValue - thresholdValue);
        const { value: formattedDiffValue, metric: formattedDiffMetric } = formatMetricValue(diff, metric);

        return {
          type,
          severity,
          direction,
          operator,
          metric,
          value: thresholdValue,
          otherValue: metricValue,
          diff,
          rawComparisonValue: comparisonValue,
          over,
          percentage,
          signedPercentage: (over ? 1 : -1) * percentage,
          formatted: `${formattedValue} ${formattedMetric}`,
          formattedValue,
          formattedMetric,
          formattedOther: `${formattedOtherValue} ${formattedOtherMetric}`,
          formattedDiff: `${formattedDiffValue} ${formattedDiffMetric}`,
          toString() {
            return this.formatted;
          },
          ratio
        };
      })
      .filter(Boolean);
  }

  getAlertThresholdValueForMetric(alert, metric, thresholdOverride) {
    const alertModel = alert?.get ? alert : new AlertModel(alert);

    metric = metric || this.getMetrics(alertModel)[0] || 'bits';

    // If legacy alert, we need to make sure we're deriving the metric properly
    const thresholdValues = this.getAlertThresholdValues(alertModel, thresholdOverride).filter(
      (threshold) => threshold.metric === metric && Number.isFinite(threshold.value)
    );

    return minBy(thresholdValues, (t) => t.percentage);
  }

  checkInterfaces() {
    return api.get('/api/ui/ddos/precheck').then(({ interfaceCount, classifiedCount, externalInterfaceCount }) => ({
      externalInterfaceCount,
      percentClassified: Math.round((classifiedCount / interfaceCount) * 100)
    }));
  }

  checkTrafficHistory() {
    return api
      .post('/api/ui/ddos/traffic-history')
      .then(({ results }) => results.reduce((acc, result) => acc + result.in_bytes + result.out_bytes, 0));
  }

  getPolicyAlarmCount(ruleId, lookback = 604800) {
    return api.get(`/api/ui/alertingManager/alarms/count?ruleId=${ruleId}&lookback=${lookback}`);
  }

  defaultExportColumns() {
    const columns = [
      'status',
      'severity',
      'applicationLabel',
      'vector',
      'primaryDimension',
      'metric',
      'alarmId',
      'startTime',
      'duration'
    ];

    if (this.store.$mkp.tenants.tenantAlertingOptions.length > 0) {
      columns.splice(4, 0, 'tenant');
    }

    return columns;
  }

  getExportColumns(showTenantAlerts) {
    return [
      {
        label: 'Alert ID',
        name: 'id',
        id: 'id'
      },
      {
        label: 'Alert State',
        name: 'stateLabel',
        id: 'stateLabel'
      },

      {
        label: 'Severity',
        name: 'severity',
        id: 'severity'
      },
      {
        label: 'Type',
        name: 'applicationLabel',
        id: 'applicationLabel'
      },

      {
        label: 'Policy',
        name: 'policyName',
        id: 'policyName'
      },

      {
        label: 'Policy ID',
        name: 'policyID',
        id: 'policyID'
      },

      !showTenantAlerts || this.store.$mkp.tenants.tenantAlertingOptions.length === 0
        ? undefined
        : {
            label: 'Tenant',
            name: 'tenant',
            id: 'tenant'
          },

      {
        label: 'Dimensions',
        name: 'primaryDimension',
        id: 'primaryDimension'
      },

      {
        label: 'Metric',
        name: 'metric',
        id: 'metric'
      },

      {
        label: 'Mitigation ID',
        name: 'mitigationID',
        id: 'mitigationID'
      },

      {
        label: 'Duration',
        name: 'duration',
        id: 'duration'
      },

      {
        label: 'Time',
        name: 'startTime',
        id: 'startTime'
      }
    ].filter(Boolean);
  }

  exportCSV(exportSettings, showTenantAlerts) {
    // the rows selected (current, 200, 500 etc.) and the columns selected (current, or all)
    const { columnsSelected, rowsSelected } = exportSettings;

    // get the filter as it will be needed to get the same results for n number of rows
    const currentFilters = this.collection.getServerFilter();

    // get the currently visible column configuration as it's needed if the user wants only current configured columns
    const { columns, visibleColumns } = getTabPref(
      'alertManagerAlertingTable',
      this.getExportColumns(showTenantAlerts),
      this.defaultExportColumns
    );

    const fileName = this.store.$exports.getExportFileName(`alerts-summary-${Date.now()}`);

    const path = '/api/ui/alertingManager/alarms/export-csv';
    const type = 'csv';
    const options = { path, fileName, type };

    this.store.$exports.addLoadingExport(options);

    // post to get data in raw, txt format containing csv
    return api
      .post(path, {
        data: {
          sortBy: this.collection.getServerSortBy(),
          // send current filters
          currentFilters,
          // send the currently visible columns -- if no settings were found we just use the available ones
          currentColumns: visibleColumns.length > 0 ? visibleColumns : columns,
          // send available columns (if the user wants them all to be present)
          availableColumns: columns,
          // current number of rows loaded
          currentRowCount: this.collection.size,
          // selected option (all or current)
          columns: columnsSelected,
          // selected option (current, 200, 500 etc.)
          rows: rowsSelected
        },
        rawResponse: true
      })
      .then((response) => {
        this.store.$exports.clearLoadingExport(options);
        this.store.$exports.addPayload(response.text, options);
      });
  }

  getPolicyComparisonQuery(policyForm) {
    const {
      selected_devices,
      dimensions,
      metrics,
      selectedMeasurement,
      filters: policyFilters,
      cidr = 32,
      cidr6 = 128
    } = policyForm.getValues();
    const filters = buildFilterGroup(policyFilters);

    const query = {
      minsPolling: 1,
      forceMinsPolling: true,
      reAggInterval: 3600,
      reAggFn: 'max',
      depth: 10,
      cidr,
      cidr6,
      filters,
      lookback_seconds: 86400 * 3,
      metric: dimensions,
      show_total_overlay: false,
      show_overlay: false,
      viz_type: 'line',
      outsort: 'max_bits_per_sec',
      ...selected_devices
    };

    return metrics.map((metric) => {
      const metricOption = getMetricOption(metric, selectedMeasurement);
      const legacyUnits = this.store.$dictionary.get(`unitsLegacy.${metricOption.unit}`);

      return {
        ...query,
        query_title: `${metricOption.label} Condition`,
        units: legacyUnits ? metricOption.unit : undefined,
        aggregateTypes: legacyUnits || [metricOption.value]
      };
    });
  }

  getDashboardQuery(alarm) {
    const alarmBody = alarm.get();
    const dimensionToKeyPart =
      alarm.queryDimensionToKeyPart || alarmBody.dimensionToKeyPart || alarmBody.key?.value || {};
    const { policyObject, startTime, eventEndTime } = alarm;
    const endTimeMoment = eventEndTime
      ? moment.utc(eventEndTime).add(30, 'minutes').format(DEFAULT_DATETIME_FORMAT)
      : moment.utc().format(DEFAULT_DATETIME_FORMAT);

    const alarmFilters = {
      connector: 'All',
      filterGroups: [
        buildFilterGroup({
          filters: Object.keys(dimensionToKeyPart).map((key) => getQueryFilter(key, dimensionToKeyPart[key]))
        })
      ]
    };

    const query = {
      ...policyObject.selected_devices,
      filters: transformPolicyFilters(mergeFilterGroups(alarmFilters, policyObject.filters)),
      lookback_seconds: 0,
      starting_time: moment.utc(startTime).subtract(30, 'minutes').format(DEFAULT_DATETIME_FORMAT),
      ending_time: endTimeMoment
    };

    return query;
  }

  getEventAlertQuery(eventAlertModel) {
    const overrides = {
      query: { viz_type: 'stackedBar', show_total_overlay: false, show_overlay: false }
    };

    // Only overrides policy filters wih thresholdFilters if alert has them
    if (eventAlertModel.threshold?.filters) {
      const convertedThresholdFilters = convertThresholdFiltersToQueryFilters(eventAlertModel.threshold.filters);
      overrides.filters = mergeFilterGroups(convertedThresholdFilters, eventAlertModel.policy.filters);
    }

    if (eventAlertModel.isSyslogEventPolicy) {
      overrides.metrics = [EVENT_POLICY_METRICS.SYSLOG];
    }

    if (eventAlertModel.isSnmpTrapEventPolicy) {
      overrides.metrics = [EVENT_POLICY_METRICS.SNMP_TRAP];
    }

    return this.getAlertQuery(eventAlertModel, overrides);
  }

  getAlertQuery(alertModel, overrides = {}) {
    const policy = alertModel.policyObject;
    const dimensions = overrides.dimensions || alertModel.queryDimensions || policy.dimensions;
    const endTimeMoment = alertModel.eventEndTime
      ? moment.utc(alertModel.eventEndTime).add(30, 'minutes').format(DEFAULT_DATETIME_FORMAT)
      : moment.utc().format(DEFAULT_DATETIME_FORMAT);

    let metrics = overrides.metrics || alertModel.sortedMetrics || policy.metrics;
    metrics = metrics.map((metric) => {
      if (this.store.$dictionary.get(`unitsLegacy.${metric}`)) {
        return metric;
      }

      // if needed, translate alert metrics into the query units defined in ALERT_METRIC_OPTIONS (src/app/util/constants.js)
      const metricOption = getMetricOption(metric, undefined);
      return metricOption.unit;
    });

    const query = {
      ...this.getDashboardQuery(alertModel),
      metric: dimensions,
      aggregateTypes: uniq(flatMap(metrics, (metric) => this.store.$dictionary.get(`unitsLegacy.${metric}`) || [])),
      outsort:
        this.store.$dictionary.get(`unitsLegacy.${metrics[0]}[0]`) || this.store.$dictionary.get(`units.${metrics[0]}`),
      secondaryOutsort:
        metrics.length > 1
          ? this.store.$dictionary.get(`unitsLegacy.${metrics[1]}[0]`) ||
            this.store.$dictionary.get(`units.${metrics[1]}`)
          : null,
      viz_type: metrics.length > 1 ? 'line' : 'stackedArea',
      ending_time: endTimeMoment,
      ...(overrides.query || {})
    };

    if (overrides.filters) {
      query.filters = overrides.filters;
    }

    // adjust DE interval for policies with < 10 min windows (basically all of them)
    const evaluationPeriod = Number.isInteger(policy.evaluationPeriod)
      ? policy.evaluationPeriod
      : parseInt(policy.evaluationPeriod?.replace('s', ''), 10);
    if (evaluationPeriod < 600) {
      query.forceMinsPolling = true;
      query.minsPolling = Math.ceil(evaluationPeriod / 60);

      // adjust for SNMP policies (minimum window is 5 min)
      if (dimensions.some((dimension) => dimension.includes('__snmp__'))) {
        query.minsPolling = 5;
        query.reAggInterval = 'auto';
        query.reAggFn = 'none';
      }
    }

    return query;
  }

  // type can be various ddos factors related values, found under queries
  getDdosFactorsQuery(alertModel, type) {
    const baseQuery = ddosFactorQueries[type];
    const { startTime, endTime, policyObject } = alertModel;
    const { dimensionToKeyPart } = alertModel.get();

    const query = {
      ...baseQuery,
      // all_devices: !deviceName,
      // device_name: deviceName,
      ...policyObject.selected_devices,
      // lookback_seconds needs to be set to 0 otherwise the start and end times will be ignored
      lookback_seconds: 0,
      starting_time: moment.utc(startTime).subtract(30, 'minutes').format(DEFAULT_DATETIME_FORMAT),
      ending_time: moment.utc(endTime).format(DEFAULT_DATETIME_FORMAT),
      filters: {
        connector: 'All',
        filterGroups: []
      }
    };

    const filters = Object.keys(dimensionToKeyPart).map((key) => getQueryFilter(key, dimensionToKeyPart[key]));

    if (filters.length > 0) {
      addFilters(query, filters, 'All', [], true);
    }

    if (policyObject && policyObject.filters) {
      query.filters = mergeFilterGroups(query.filters, transformPolicyFilters(policyObject.filters));
    }

    return query;
  }

  getMetricsThresholdChartQuery = ({
    metrics,
    measurement,
    dimensionToValue,
    startTime,
    endTime,
    lookbackSeconds,
    queryOptions = {}
  }) => {
    const dimensions = Object.keys(dimensionToValue);
    const filters = {
      connector: 'All',
      filterGroups: [
        {
          name: '',
          named: false,
          connector: 'All',
          not: false,
          autoAdded: '',
          filters: dimensions.map((dimension) => {
            const value = dimensionToValue[dimension];
            return {
              filterField: dimension,
              metric: '',
              aggregate: '',
              operator: '=',
              filterValue: typeof value === 'string' ? `$_kntq$${value}$_kntq$` : value
            };
          }),
          saved_filters: [],
          filterGroups: []
        }
      ]
    };

    const selectedAggFuncDefault = 'avg';
    const queryOptionsDefaults = {
      selectedDimensions: dimensions,
      selectedSortOrder: 'desc',
      selectedLimitCount: 10,
      selectedAggFunc: selectedAggFuncDefault,
      selectedWindowSize: measurement === '/protocols/bgp/neighbors/prefixes' ? 300 : 0,
      selectedTransformation: 'none',
      selectedRollupsLimit: 10,
      selectedRollupsAggFunc: ['min', 'avg', 'max', 'last'],
      selectedVisualization: 'area',
      filters
    };

    const queryOptionsMerged = { ...queryOptionsDefaults, ...queryOptions };

    if (lookbackSeconds >= 0) {
      queryOptionsMerged.lookback_seconds = lookbackSeconds;
    } else {
      // Negative numbers represent +/- time margins on an event.
      const bufferInSeconds = Math.abs(lookbackSeconds);
      queryOptionsMerged.starting_time = moment
        .utc(startTime)
        .subtract(bufferInSeconds, 'seconds')
        .format(DEFAULT_DATETIME_FORMAT);
      queryOptionsMerged.ending_time = moment.utc().format(DEFAULT_DATETIME_FORMAT);
      if (endTime) {
        const end = moment.utc(endTime).add(bufferInSeconds, 'seconds');
        const now = moment.utc();

        queryOptionsMerged.ending_time = (end > now ? now : end).format(DEFAULT_DATETIME_FORMAT);
      }
    }

    // If the queryOptionsMerged.selectedAggFunc is NOT the default, ensure that width of the query
    // window, in seconds, is larger than a maximum allowed value.  If it is, reset it to the
    // default value.
    const maxQueryWindowSeconds = 21600;
    if (queryOptionsMerged.selectedAggFunc !== selectedAggFuncDefault) {
      let duration = 0;
      let success = true;

      if (queryOptionsMerged.lookback_seconds >= 0) {
        duration = queryOptionsMerged.lookback_seconds;
      } else {
        const result = this.getDurationInSeconds(queryOptionsMerged?.starting_time, queryOptionsMerged?.ending_time);
        duration = result.duration;
        success = result.success;
      }

      if (!success || duration > maxQueryWindowSeconds) {
        // If we encounter an error or the window is wider than our defined limits to reset the agg
        // func to the default.
        queryOptionsMerged.selectedAggFunc = selectedAggFuncDefault;
      }
    }

    return this.store.$metrics.getFullMetricsQuery({ metrics, measurement, queryOptions: queryOptionsMerged });
  };

  getDurationInSeconds(startDateString, endDateString) {
    let endDate;
    if (endDateString === null || endDateString === undefined || endDateString === '') {
      endDate = Date.now();
    } else {
      endDate = new Date(endDateString);
    }
    const startDate = new Date(startDateString);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return { success: false, duration: Number.MAX_SAFE_INTEGER, errorMessage: 'Invalid date format' };
    }

    const durationInMilliseconds = Math.abs(endDate - startDate);
    const durationInSeconds = Math.floor(durationInMilliseconds / 1000);

    return { success: true, duration: durationInSeconds };
  }

  getDimensionLabel(dimension) {
    return (
      DIMENSION_TO_LABEL[dimension] ||
      this.store.$dictionary.get(
        `chartTypesValidations.${deserializeCidrDimension(dimension, this.store.$dictionary.get('cidrMetrics'))}`
      ) ||
      startCase(dimension)
    );
  }

  handleTotalCountLoad = ({ filters, skipReset = false, isExport = false } = { skipReset: false, isExport: false }) => {
    const totalCountIncludedAlertCollection = new AlertCollection();

    if (!skipReset) {
      this.collection.resetPageState();
    }

    return totalCountIncludedAlertCollection
      .setServerFilter(
        filters || {},
        { force: true },
        {
          data: {
            offset: isExport ? 0 : this.collection.pageState.currentPage * this.collection.pageState.perPage,
            limit: isExport ? ALERTING_EXPORT_PER_PAGE_COUNT_DEFAULT : this.collection.pageState.perPage,
            pagination: { includeTotalCount: true }
          }
        }
      )
      .then((result) => {
        if (result) {
          const totalCount = parseInt(result.pagination?.total_count || this.collection.pageState.perPage);

          this.collection.setPageState({
            pageCountLoaded: true,
            totalCount,
            pageCount: Math.ceil(totalCount / this.collection.pageState.perPage) || 1
          });
        }

        return result;
      });
  };
}

export default new AlertingStore();
